import { getLogLevel, LogLevel } from "../../../Utilities/Platform/Logging";
import { getOption, setOption } from "../../../Utilities/Platform/Options/Options";
import { type DISPLAY_FROM_SHOPFRONT, DISPLAY_TO_SHOPFRONT } from "../DisplayEvents";
import BaseDisplayDriver, { DisplayDriverEvents } from "./BaseDisplayDriver";

export default class PresentationDisplayDriver extends BaseDisplayDriver {
    protected connection: PresentationConnection | undefined;

    constructor(
        protected presentationRequest: PresentationRequest
    ) {
        super();

        this.onConnectionClose = this.onConnectionClose.bind(this);
        this.onConnectionMessage = this.onConnectionMessage.bind(this);
        this.onNewConnection = this.onNewConnection.bind(this);

        this.presentationRequest.addEventListener("connectionavailable", this.onNewConnection);
    }

    protected onNewConnection(e: Event): void {
        const connection = (e as PresentationConnectionAvailableEvent).connection;

        // If connection already exists, terminate
        if(this.connection) {
            this.connection.close();
        }

        this.connection = connection;

        // Set the local storage id
        setOption("customer_display_connection_id", connection.id);

        // Bind event listeners
        this.connection.addEventListener("close", this.onConnectionClose);
        this.connection.addEventListener("terminate", this.onConnectionClose);
        this.connection.addEventListener("message", this.onConnectionMessage);

        this.emit(DisplayDriverEvents.DRIVER_OPEN);
    }

    protected onConnectionClose(): void {
        this.emit(DisplayDriverEvents.DRIVER_CLOSE);
    }

    protected onConnectionMessage(e: Event): void {
        const data = JSON.parse((e as MessageEvent).data);

        if(Array.isArray(data)) {
            return;
        }

        const { type, body } = data;

        if(typeof type !== "string") {
            return;
        }

        if(!(type in DISPLAY_TO_SHOPFRONT)) {
            return;
        }

        this.emit(DisplayDriverEvents.MESSAGE, {
            type: type as DISPLAY_TO_SHOPFRONT,
            body,
        });
    }

    public async init(): Promise<void> {
        // Check if display is already open
        const connectionId = getOption("customer_display_connection_id");

        if(connectionId) {
            try {
                await this.presentationRequest.reconnect(connectionId);
            } catch(err) {
                setOption("customer_display_connection_id", "");
                console.error(
                    "Cannot reconnect to customer display, assuming that the display has been closed",
                    err
                );

                this.onConnectionClose();
            }
        }
    }

    public async close(): Promise<void> {
        if(this.connection) {
            this.connection.terminate();
            this.connection = undefined;
        }

        setOption("customer_display_connection_id", "");
    }

    public async open(): Promise<boolean> {
        if(
            this.connection && (
                this.connection.state === "connected" ||
                this.connection.state === "connecting"
            )
        ) {
            // Already open
            return true;
        }

        if(getLogLevel() === LogLevel.VERBOSE) {
            console.log("Attempting to open customer display using the presentation API");
        }

        navigator.presentation.defaultRequest = this.presentationRequest;

        try {
            await this.presentationRequest.start();

            return true;
        } catch(err) {
            console.error("Error connecting to customer display", err);

            return false;
        }
    }

    public async sendMessage(type: DISPLAY_FROM_SHOPFRONT, body: unknown): Promise<void> {
        if(!this.connection) {
            // Customer Display is closed
            return;
        }

        if(this.connection.state !== "connected") {
            if(this.connection.state === "terminated") {
                setOption("customer_display_connection_id", "");
                this.connection = undefined;
            }

            throw new Error("Cannot send customer display message, connection is not open");
        }

        this.connection.send(JSON.stringify({
            type,
            body,
        }));
    }

    public static canUsePresentationAPI(): boolean {
        return typeof PresentationRequest !== "undefined";
    }
}
